import { Modal } from "antd";
import { Box, Text, Flex } from "@chakra-ui/react";
import TableItensCotados from "./components/TableDetalhesItensCotados/index";
import { useDetalhesItemCotacao } from "core/features/orcamento/hooks/useDetalhesItemCotacao";
import { CotacaoAprovarItemsForm } from "core/features/orcamento/typings";
import { useLoading } from "components/GlobalLoading/index";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import FooterModal from "ui/components/Button/FooterModal";
import { formatMoney } from "infra/helpers/number";
import colors from "core/resources/theme/colors";
import useAprovarItemsCotacao from "core/features/orcamento/hooks/useAprovarItemsCotacao";
import { showSuccess } from "infra/helpers/alerta";

interface ModalDetalhesCotacaoProps {
  isOpenModal: boolean;
  onClose: () => any;
  orcamentoId?: string;
  cotacaoId?: string;
  refetch?: () => void;
}

function ModalDetalhesCotacao({
  isOpenModal,
  onClose,
  cotacaoId,
  orcamentoId,
  refetch,
}: ModalDetalhesCotacaoProps) {
  const { handleSubmit, reset, watch } = useForm<{
    items: CotacaoAprovarItemsForm[];
  }>({
    defaultValues: {
      items: [],
    },
  });

  const { data, isLoading } = useDetalhesItemCotacao(orcamentoId, cotacaoId);
  const {
    mutate: aprovarItemsCotacao,
    isLoading: isLoadingAprovarItemsCotacao,
  } = useAprovarItemsCotacao(orcamentoId, cotacaoId);

  const onSubmit = (data: { items: CotacaoAprovarItemsForm[] }) => {
    aprovarItemsCotacao(data, {
      onSuccess: () => {
        showSuccess().then(() => {
          onClose();
          refetch?.();
        });
      },
    });
  };

  useEffect(() => {
    if (data) {
      reset({
        items: data?.orcamentoCotacaoItems?.map((item) => ({
          id: item.id,
          aprovado: !!item.aprovadoEm,
          total: item.total,
        })),
      });
    }
  }, [data, reset]);

  const form = watch("items");

  const algumItemAprovado = !!form?.find((item) => item.aprovado);
  const isCheckedAll = form?.every((item) => item.aprovado);
  const isIndeterminate = algumItemAprovado && !isCheckedAll;
  const checkedItems = form
    ?.filter((item) => item.aprovado)
    .map((item) => item.id);

  const totalAprovado = form
    .filter((item) => !!item.aprovado)
    .reduce((acc, item) => acc + item.total, 0);

  const totalNaoAprovado = form
    .filter((item) => !item.aprovado)
    .reduce((acc, item) => acc + item.total, 0);

  const onCheckItem = (itemId: string, aprovado: boolean) => {
    const index = form.findIndex((item) => item.id === itemId);

    if (index !== -1) {
      const newForm = [...form];
      newForm[index].aprovado = aprovado;
      reset({ items: newForm });
    }
  };

  const onCheckAll = () => {
    const newForm = form.map((item) => ({ ...item, aprovado: !isCheckedAll }));
    reset({ items: newForm });
  };

  useLoading(isLoading);
  return (
    <Modal
      title={`Aprovar itens da cotação - ${data?.nomeFornecedor ?? ""}`}
      centered
      visible={isOpenModal}
      footer={
        <FooterModal
          registerAnother={false}
          setRegisterAnother={() => null}
          onSubmit={handleSubmit(onSubmit)}
          onClose={onClose}
          showRegisterAnother={false}
          primaryButtonText="Salvar"
          buttonCancelarText="Fechar"
          isLoadingSave={isLoadingAprovarItemsCotacao}
          isDisabled={isLoadingAprovarItemsCotacao || isLoading}
        />
      }
      onCancel={onClose}
      destroyOnClose
      width="1200px"
    >
      <Box>
        {data?.orcamentoCotacaoItems?.length ? (
          <>
            <TableItensCotados
              onCheckItem={onCheckItem}
              onCheckAll={onCheckAll}
              isCheckedAll={isCheckedAll}
              isIndeterminate={isIndeterminate}
              checkedItems={checkedItems}
              data={data?.orcamentoCotacaoItems}
            />

            <Flex
              flexDirection="column"
              alignItems="end"
              mt={2}
              h="40px"
              mr={4}
            >
              <Text
                fontSize="14px"
                fontWeight="medium"
                mb={2}
                color={colors.accentPositive}
              >
                Total aprovado: {formatMoney(totalAprovado ?? 0)}
              </Text>

              <Text
                fontSize="14px"
                fontWeight="medium"
                color={colors.accentNegative}
              >
                Total não aprovado: {formatMoney(totalNaoAprovado ?? 0)}
              </Text>
            </Flex>
          </>
        ) : null}
      </Box>
    </Modal>
  );
}

export default ModalDetalhesCotacao;
